import { criterion, fromCriteria, unsupported } from "@/core/check";
import type { Lesson } from "@/core/types";
import { isTrackingOn, readChangeTrackingMode } from "@/office/word/review";

export const lektionNachverfolgen: Lesson = {
  id: "aenderungen-nachverfolgen",
  title: "Änderungen nachverfolgen",
  summary: "Word mitschreiben lassen, wer was geändert hat.",
  durationMinutes: 9,
  learningGoals: [
    "Die Änderungsnachverfolgung ein- und ausschalten",
    "Verstehen, was Word dabei aufzeichnet",
    "Die Anzeigeeinstellungen richtig deuten",
  ],
  steps: [
    {
      id: "einschalten",
      title: "Die Nachverfolgung einschalten",
      explanation: [
        "Kommentare sagen, was geändert werden sollte. Die Änderungsnachverfolgung geht " +
          "einen Schritt weiter: Du änderst den Text tatsächlich, und Word zeichnet jede " +
          "Änderung auf – wer sie wann vorgenommen hat und was vorher dastand.",
        "Die verfassende Person entscheidet danach für jede einzelne Änderung, ob sie " +
          "übernommen oder verworfen wird. Nichts geht verloren, nichts passiert " +
          "unbemerkt.",
        "Eingeschaltet wird das unter „Überprüfen“ → „Änderungen nachverfolgen“ oder mit " +
          "Strg + Umschalt + V. Wichtig: Der Schalter wirkt ab dem Moment des " +
          "Einschaltens – vorher gemachte Änderungen erfasst er nicht rückwirkend.",
      ],
      keyPoints: [
        "Strg + Umschalt + V schaltet die Nachverfolgung um.",
        "Die Aufzeichnung beginnt erst beim Einschalten.",
        "Der Schalter wird im Dokument gespeichert und gilt auch für andere.",
      ],
      task: {
        instruction: "Schalte in deinem Dokument die Änderungsnachverfolgung ein.",
        hint: "Überprüfen → Änderungen nachverfolgen. Die Schaltfläche bleibt hervorgehoben, solange sie aktiv ist.",
        check: async () => {
          const modus = await readChangeTrackingMode();
          if (modus === null) {
            return unsupported(
              "Diese Word-Version stellt dem Add-in den Zustand der Änderungsnachverfolgung " +
                "nicht zur Verfügung.",
              "Prüfe selbst, ob die Schaltfläche unter „Überprüfen“ hervorgehoben ist, und markiere den Schritt dann als erledigt.",
            );
          }

          return fromCriteria(
            [
              criterion(
                `Die Änderungsnachverfolgung ist aktiv (Zustand: ${modus})`,
                isTrackingOn(modus),
              ),
            ],
            {
              success: "Ab jetzt zeichnet Word jede Änderung am Text auf.",
              failure: "Die Änderungsnachverfolgung ist noch ausgeschaltet.",
              hint: "Die Schaltfläche sitzt auf der Registerkarte „Überprüfen“ in der Gruppe „Nachverfolgung“.",
            },
          );
        },
      },
    },
    {
      id: "markup-verstehen",
      title: "Was die Anzeige bedeutet",
      explanation: [
        "Jetzt zur häufigsten Verwirrung. Neben der Schaltfläche steht ein Auswahlfeld " +
          "mit vier Einstellungen: „Markup: alle“, „Einfaches Markup“, „Kein Markup“ und " +
          "„Original“.",
        "Diese Auswahl ändert nur die Anzeige, nicht das Dokument. „Kein Markup“ zeigt, " +
          "wie der Text nach Annahme aller Änderungen aussähe – die Änderungen sind aber " +
          "weiterhin im Dokument gespeichert.",
        "Genau hier passiert der teure Fehler: Jemand stellt auf „Kein Markup“, sieht ein " +
          "sauberes Dokument und verschickt es. Beim Empfänger steht die Anzeige auf " +
          "„Markup: alle“ – und der sieht jede verworfene Formulierung und jeden internen " +
          "Kommentar.",
      ],
      keyPoints: [
        "Die Markup-Auswahl ändert nur die Ansicht, nie das Dokument.",
        "„Kein Markup“ heißt nicht „keine Änderungen mehr enthalten“.",
        "Vor dem Versand die Änderungen wirklich annehmen oder ablehnen.",
      ],
      task: {
        instruction:
          "Schreibe bei eingeschalteter Nachverfolgung einen zusätzlichen Satz in dein " +
          "Dokument und schau dir die Markierung an. Probiere anschließend die " +
          "Anzeigeeinstellungen „Markup: alle“ und „Kein Markup“ aus.",
        hint: "Achte darauf, dass die Nachverfolgung wirklich eingeschaltet ist, bevor du schreibst.",
        check: async () => {
          const modus = await readChangeTrackingMode();
          if (modus === null) {
            return unsupported(
              "Diese Word-Version stellt dem Add-in den Zustand der Änderungsnachverfolgung " +
                "nicht zur Verfügung.",
              "Markiere den Schritt als erledigt, sobald du beide Ansichten ausprobiert hast.",
            );
          }

          return fromCriteria(
            [
              criterion(
                `Die Nachverfolgung ist weiterhin aktiv (Zustand: ${modus})`,
                isTrackingOn(modus),
              ),
            ],
            {
              success: "Gut – deine Änderung ist damit nachvollziehbar aufgezeichnet.",
              failure: "Die Nachverfolgung wurde zwischendurch wieder ausgeschaltet.",
              hint: "Schalte sie erneut ein und schreibe den Satz danach.",
            },
          );
        },
      },
    },
  ],
};
